import { useState, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import LocationArrowSVG from '../../assets/imgs/icon/location-arrow.svg';

import { Button } from '../Button';
import { Flex } from '../Flex';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Modal } from '../Modal';
// import DOORDASH from '../../assets/imgs/doordash.svg';
import DRIVETHRU from '../../assets/imgs/drivethru.svg';
import CLOSE from '../../assets/imgs/icon/close.svg';
// import SELECTED from '../../assets/imgs/icon/selected.svg';
import INFO from '../../assets/imgs/info.svg';
import MOBILE from '../../assets/imgs/mobile.svg';
import STAR_ACTIVE_SVG from '../../assets/imgs/star-active.svg';
import WIFI from '../../assets/imgs/wifi.svg';
import { StoreFieldsFragment } from '../../graphql';
import { RootStates } from '../../reduxstore';
import { useAppSelector } from '../../reduxstore/hooks';
import { storeIdState } from '../../Store/atoms';
import { HoursWarning } from './HoursWarning';
import { SelectButton } from './SelectButton';
import { StoreSelectionSource } from '../StoreSelector/type';
import { getStoreOpenStatus } from '../../Store/utils/getStoreOpenStatus';
import { getNow } from '../../utils/getNow';

export const StoreCard = ({
	forSettingOLOStore,
	source,
	store,
	isPrimary,
	onRemove,
	buttonText = 'SELECT',
	buttonAction,
	setPrimaryStore
}: IStoreCard): JSX.Element => {
	const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
	// const [storeId, setStoreIds] = useState<number>(0);
	const selectedStoreId = useRecoilValue(storeIdState);
	// const dispatch = useAppDispatch();
	// const location = useLocation();
	const authenticated = useAppSelector(
		(state: RootStates) => state.authReducer.id
	);

	const favoriteStoresIds = useAppSelector((state: RootStates) =>
		state.accountReducer.profile.favorite_stores.map(
			storeDetails => storeDetails.store
		)
	);

	const togglePopover = () => {
		setPopoverOpen(!popoverOpen);
	};

	const now = getNow();
	const storeOpenStatus = useMemo(
		() => getStoreOpenStatus(store, now),
		[store, now]
	);

	// const [getUnavailableProduct] = useGetUnavailableProductsLazyQuery({
	// 	fetchPolicy: 'network-only',
	// 	onCompleted: (data: GetUnavailableProductsQuery) => {
	// 		dispatch(
	// 			setUnavailableProductIds(
	// 				data.unavailableProducts.map(product => product.productId)
	// 			)
	// 		);
	// 		buttonAction(storeId);
	// 	}
	// });

	// const setStoreId = (storeId: number) => {
	// 	setStoreIds(storeId);
	// 	// This is to avoid multiple unavailable product queries while selecting store from favorites
	// 	const isStoreSelectedInFavorites = location.pathname
	// 		.toLowerCase()
	// 		.includes('/account/favorites');
	// 	if(isStoreSelectedInFavorites) buttonAction(storeId); else getUnavailableProduct({ variables: { storeId } });
	// };

	const findLocation = () => {
		window.open(
			`https://maps.google.com/?q=BIGGBY Coffee, ${store.address1} ${
				store.address2 || ''
			}, ${store.city}, ${store.state} ${store.zipCode}`,
			'_blank',
			'noopener noreferrer'
		);
	};

	return (
		<>
			<FavoriteStore>
				<Flex
					$flexDirection="row"
					$placeContent="flex-start space-between"
					$alignItems="flex-start"
					$columnGap="10px"
				>
					<Flex $flexDirection="column">
						<Header>{`${store.city}`}</Header>
						<StoreInfo>{`${store.address1} ${
							store.address2 || ''
						}`}</StoreInfo>
						<StoreInfo>
							{`${store.city}, ${store.state} ${store.zipCode}`}
						</StoreInfo>
					</Flex>
					<Actions>
						{selectedStoreId === store.storeId &&
							source !== 'FAVORITE_LIST' && (
								<Selected>SELECTED</Selected>
							)}
						{authenticated &&
							source !== 'FAVORITE_LIST' &&
							favoriteStoresIds.includes(store.storeId) && (
								<Icon
									$url={STAR_ACTIVE_SVG}
									alt="favorite"
									$title="Favorite Store"
								/>
							)}

						{onRemove && (
							<IconButton
								url={CLOSE}
								alt="remove"
								width={18}
								height={18}
								onClick={onRemove}
								title={'Remove Favorite Store'}
							/>
						)}
					</Actions>
				</Flex>

				<Flex
					$flexDirection="row"
					$placeContent={
						source === 'FAVORITE_LIST'
							? 'center space-between'
							: 'center flex-end;'
					}
					$alignItems="center"
					$padding="8px 0 8px 0"
				>
					{isPrimary && (
						<Primary onClick={() => togglePopover()}>
							<div>Primary Store</div>
							<IconButton
								url={INFO}
								alt="info"
								width={16}
								height={16}
							/>
						</Primary>
					)}
					{source === 'FAVORITE_LIST' && !isPrimary && (
						<TextButton
							onClick={() =>
								setPrimaryStore &&
								setPrimaryStore(store.storeId)
							}
						>
							Set as Primary Store
						</TextButton>
					)}
					<Flex
						$flexDirection="column"
						$placeContent="flex-end center"
						$alignItems="flex-end"
						$width="unset"
					>
						{store.distance && (
							<Distance>
								{Math.round((store.distance ?? 0) * 10) / 10} mi
							</Distance>
						)}

						<HoursWarning storeOpenStatus={storeOpenStatus} store={store} />
					</Flex>
				</Flex>
				<Flex
					$flexDirection="row"
					$placeContent="center space-between"
					$alignItems="center"
				>
					<Icons>
						{store.wiFi && (
							<Icon $url={WIFI} alt="wifi" $title="Free Wifi" />
						)}
						{store.driveThru && (
							<Icon
								$url={DRIVETHRU}
								alt="drive thru"
								$title="Drive Thru"
							/>
						)}
						{/* <IconButton url={DOORDASH} alt="doordash" /> */}
						{store.oLOEnabled && (
							<Icon
								$url={MOBILE}
								alt="online_ordering"
								$title="Online Ordering"
							/>
						)}
					</Icons>
					<Buttons>
						<Button
							$minWidth="60px"
							$maxWidth="60px"
							onClick={findLocation}
						>
							<img
								src={LocationArrowSVG}
								style={{ width: '14px', height: '14px' }}
							/>
						</Button>
						{forSettingOLOStore && storeOpenStatus === 'OPEN'  && store.oLOEnabled ? <SelectButton
							buttonText={buttonText}
							buttonAction={() => {
								buttonAction(store.storeId);
							}}
							forSettingOLOStore={forSettingOLOStore}
							oLOEnabled={store.oLOEnabled}
							isDisabled={
								source === 'FAVORITE_MODEL' &&
								favoriteStoresIds.includes(store.storeId)
							}
						/> : null}
						
					</Buttons>
				</Flex>
			</FavoriteStore>
			<Modal
				isOpen={popoverOpen}
				toggleHandler={() => togglePopover()}
				size="md"
			>
				<p>
					Your primary store is the store you will receive coupons and
					special offers from.
				</p>
				<Flex $flexDirection="row" $justifyContent="flex-end">
					<Button
						$buttonSize="small"
						type="button"
						onClick={() => togglePopover()}
					>
						Close
					</Button>
				</Flex>
			</Modal>
		</>
	);
};

const FavoriteStore = styled.div`
	padding: 12px 0 12px 0;
	border-bottom: 1px solid #d9d9d9;
`;

const Header = styled.div`
	line-height: 100%;
	font-size: 20px;
	margin-bottom: 8px;
	font-weight: 700;
	color: #757575;
`;

const TextButton = styled.div`
	font-size: 16px;
	font-weight: 700;
	color: #f47920;
	&:hover {
		cursor: pointer;
	}
`;

const StoreInfo = styled.div`
	color: #757575;
	line-height: 100%;
	font-weight: 700;
	font-size: 14px;
`;

const Icons = styled(Flex)`
	flex-direction: row;
	width: 90%;
	flex-wrap: wrap;
	align-items: flex-end;
	column-gap: 8px;
`;

const Actions = styled(Flex)`
	width: fit-content;
	align-items: flex-start;
	justify-content: flex-end;
	column-gap: 10px;
`;

const Buttons = styled(Flex)`
	flex-direction: row;
	column-gap: 8px;
	align-items: flex-end;
	justify-content: flex-end;
`;

const Distance = styled.span`
	font-size: 16px;
	line-height: 100%;
	font-weight: bold;
	color: #757575;
	margin-bottom: 0 !important;
`;

const Primary = styled(Flex)`
	flex-direction: row;
	align-items: center;
	width: unset;
	margin-bottom: 8px;
	font-size: 16px;
	font-weight: 700;
	column-gap: 4px;
	color: #f47920;

	&:hover {
		cursor: pointer;
	}
`;

const Selected = styled.span`
	font-size: 14px;
	font-weight: 600;
	color: #f47920;
`;

export type IStoreCard = {
	forSettingOLOStore?: boolean;
	source?: StoreSelectionSource;
	store: StoreFieldsFragment;
	isPrimary?: boolean;
	buttonEnabled?: boolean;
	buttonText?: string;
	buttonAction: (storeId: number) => void;
	toggleHandler?: () => void;
	onRemove?: () => void;
	setPrimaryStore?: (id: number) => void;
};
